import PublicHeader from '../components/PublicHeader';
// LoginPage: Auth for Super Admin, Admin, Staff, Public User
import React, { useState } from 'react';
import { Box, Typography, TextField, Button, MenuItem, Paper } from '@mui/material';
import axios from 'axios';
import api from '../services/api';

// Role selection removed; role will be determined by backend authentication

function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  // Role state removed
  const [recoveryOpen, setRecoveryOpen] = useState(false);
  const [recoveryEmail, setRecoveryEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const handleRecovery = (e) => {
    e.preventDefault();
    // TODO: Call backend /auth/recover with recoveryEmail
    setRecoveryOpen(false);
    setRecoveryEmail('');
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const res = await api.post('/api/auth/login', { email, password });
      // Save JWT to localStorage
      localStorage.setItem('token', res.data.token);
      // Optionally save user info
      // Normalize roles: prefer 'admin' if present when multiple roles exist
      const normalizeRoles = (r) => {
        if (!r) return [];
        if (Array.isArray(r)) return r.map(x => String(x).toLowerCase());
        if (typeof r === 'string') return r.split(/[,;|]/).map(x => String(x).trim().toLowerCase()).filter(Boolean);
        return [String(r).toLowerCase()];
      };
      const userObj = { ...(res.data.user || {}) };
      const roles = normalizeRoles(userObj.role);
      // If user has both superadmin and admin, prefer admin for frontend display
      if (roles.includes('admin')) userObj.role = 'admin';
      else if (roles.includes('superadmin')) userObj.role = 'superadmin';
      else if (roles.includes('staff')) userObj.role = 'staff';
      else userObj.role = roles[0] || userObj.role;
      localStorage.setItem('user', JSON.stringify(userObj));
      // Redirect based on effective role
      const eff = userObj.role;
      if (eff === 'superadmin' || eff === 'admin') window.location.href = '/admin';
      else if (eff === 'staff') window.location.href = '/staff';
      else window.location.href = '/';
    } catch (err) {
      setError(err.response?.data?.error || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const [settings, setSettings] = useState(null);
  React.useEffect(() => {
    api.get('/api/settings')
      .then(({ data }) => setSettings(data.data || data))
      .catch(() => setSettings(null));
  }, []);
  const API_BASE = (process.env.REACT_APP_API_URL || '').replace(/\/+$/, '');
  const logoSrc = settings?.logoUrl || `${API_BASE}/api/public/logo`;
  const siteName = settings?.platformName || 'Expo Platform';
  return (
    <Box sx={{
      minHeight: '100vh',
      bgcolor: 'background.default',
      background: 'linear-gradient(120deg, #e3f0ff 0%, #f5f6fa 100%)',
      display: 'flex',
      flexDirection: 'column',
    }}>
      <PublicHeader />
      <Box sx={{
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '80vh',
        px: { xs: 1, md: 6 },
        gap: { md: 6 },
      }}>
        {/* Left: Login Form */}
        <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%' }}>
          <Paper elevation={12} sx={{
            p: { xs: 3, md: 5 },
            borderRadius: 6,
            maxWidth: 400,
            width: '100%',
            bgcolor: 'background.paper',
            boxShadow: '0 8px 32px rgba(25, 118, 210, 0.10)',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            border: '1px solid #e3eafc',
            alignItems: 'center',
          }}>
            <img src={logoSrc} alt={siteName} style={{ height: 56, width: 56, objectFit: 'contain', marginBottom: 8, borderRadius: 6, background: '#fff', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }} />
            <Typography variant="h4" fontWeight="bold" align="center" gutterBottom color="primary" tabIndex={0} aria-label="Sign in" sx={{ letterSpacing: 0.5, mb: 1 }}>
              Sign in to {siteName}
            </Typography>
            <Typography variant="body2" color="text.secondary" align="left" sx={{ mb: 2, fontWeight: 500, letterSpacing: 0.2 }}>
              Forgot account? <Button variant="text" color="primary" sx={{ textTransform: 'none', fontWeight: 600, fontSize: '1rem', p: 0, minWidth: 0 }} onClick={() => setRecoveryOpen(true)}>Recover it</Button>
            </Typography>
            {/* Recovery Pop-up Dialog */}
            {recoveryOpen && (
              <Box sx={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', bgcolor: 'rgba(0,0,0,0.25)', zIndex: 1300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Paper elevation={8} sx={{ p: 4, borderRadius: 3, maxWidth: 340, width: '100%', bgcolor: 'background.paper', boxShadow: 12 }}>
                  <Typography variant="h6" fontWeight="bold" color="primary" align="center" sx={{ mb: 2 }}>
                    Account Recovery
                  </Typography>
                  <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 2 }}>
                    Enter your email to recover your account.
                  </Typography>
                  <form onSubmit={handleRecovery}>
                    <TextField label="Email address" type="email" fullWidth id="recoveryEmail" name="recoveryEmail" sx={{ mb: 2 }} value={recoveryEmail} onChange={e => setRecoveryEmail(e.target.value)} required autoFocus />
                    <Button variant="contained" color="primary" type="submit" fullWidth sx={{ py: 1, fontWeight: 700, borderRadius: 2 }}>
                      Recover Account
                    </Button>
                    <Button variant="text" color="secondary" fullWidth sx={{ mt: 1 }} onClick={() => setRecoveryOpen(false)}>
                      Cancel
                    </Button>
                  </form>
                </Paper>
              </Box>
            )}
            <form onSubmit={handleLogin} aria-label="Login form">
              <TextField
                label="Email address"
                type="email"
                fullWidth
                id="email"
                name="email"
                sx={{ mb: 2, bgcolor: '#f5f6fa', borderRadius: 2, input: { color: 'primary.main', fontWeight: 500 } }}
                value={email}
                onChange={e => setEmail(e.target.value)}
                required
                autoFocus
                inputProps={{ 'aria-label': 'Email address' }}
              />
              <TextField
                label="Password"
                type="password"
                fullWidth
                id="password"
                name="password"
                sx={{ mb: 2, bgcolor: '#f5f6fa', borderRadius: 2, input: { color: 'primary.main', fontWeight: 500 } }}
                value={password}
                onChange={e => setPassword(e.target.value)}
                required
                inputProps={{ 'aria-label': 'Password' }}
              />
              {error && <Typography color="error" sx={{ mb: 2 }}>{error}</Typography>}
              <Button
                variant="contained"
                color="primary"
                type="submit"
                fullWidth
                sx={{
                  py: 1.2,
                  fontWeight: 700,
                  fontSize: '1.1rem',
                  borderRadius: 3,
                  boxShadow: '0 4px 16px rgba(25, 118, 210, 0.10)',
                  mb: 2,
                  textTransform: 'none',
                  letterSpacing: 0.5,
                }}
                aria-label="Sign in"
                disabled={loading}
              >
                {loading ? 'Signing in...' : 'Sign in'}
              </Button>
            </form>
            <Typography align="center" color="text.secondary" sx={{ my: 2, fontSize: '0.95rem', fontWeight: 500 }}>Or continue with</Typography>
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mb: 1 }}>
              <Button variant="outlined" color="inherit" sx={{ minWidth: 48, borderRadius: 3, p: 1.2, bgcolor: '#f5f6fa', boxShadow: '0 2px 8px rgba(25, 118, 210, 0.07)' }} aria-label="Sign in with Google">
                <img src="/images/google-icon.svg" alt="Google" style={{ width: 24, height: 24 }} />
              </Button>
              <Button variant="outlined" color="inherit" sx={{ minWidth: 48, borderRadius: 3, p: 1.2, bgcolor: '#f5f6fa', boxShadow: '0 2px 8px rgba(25, 118, 210, 0.07)' }} aria-label="Sign in with Microsoft">
                <img src="/images/microsoft-icon.svg" alt="Microsoft" style={{ width: 24, height: 24 }} />
              </Button>
              <Button variant="outlined" color="inherit" sx={{ minWidth: 48, borderRadius: 3, p: 1.2, bgcolor: '#f5f6fa', boxShadow: '0 2px 8px rgba(25, 118, 210, 0.07)' }} aria-label="Sign in with GitHub">
                <img src="/images/github-icon.svg" alt="GitHub" style={{ width: 24, height: 24 }} />
              </Button>
            </Box>
          </Paper>
        </Box>
        {/* Right: Testimonial */}
        <Box sx={{ flex: 1, display: { xs: 'none', md: 'flex' }, justifyContent: 'center', alignItems: 'center', width: '100%' }}>
          <Paper elevation={0} sx={{ bgcolor: 'background.paper', p: 4, borderRadius: 4, boxShadow: 0, maxWidth: 420, width: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <img src="/images/testimonial-user.jpg" alt="User" style={{ width: 48, height: 48, borderRadius: '50%', marginRight: 12 }} />
              <Typography variant="subtitle2" color="primary" fontWeight="bold">
                <span style={{ color: '#ff6600', fontSize: '1.5rem', verticalAlign: 'middle' }}>&ldquo;</span>Game-changer
              </Typography>
            </Box>
            <Typography variant="body1" color="text.primary" sx={{ mb: 2 }}>
              Expo Platform has transformed my event management. I save time, avoid frustration, and bring my visions to life effortlessly.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              .<br />.
            </Typography>
          </Paper>
        </Box>
      </Box>
    </Box>
  );
}

export default LoginPage;
